import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { Button } from '../ui/Button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/Select';

// ----------------------------------------------------------------------

interface TablePaginationCustomProps {
  count: number;
  page: number;
  rowsPerPage: number;
  dense?: boolean;
  showDenseToggle?: boolean;
  onPageChange: (event: unknown, page: number) => void;
  onRowsPerPageChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  onChangeDense?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  rowsPerPageOptions?: number[];
}

export default function TablePaginationCustom({
  count,
  page,
  rowsPerPage,
  dense,
  showDenseToggle = true,
  onPageChange,
  onRowsPerPageChange,
  onChangeDense,
  rowsPerPageOptions = [5, 10, 25, 50]
}: TablePaginationCustomProps) {
  const totalPages = Math.ceil(count / rowsPerPage);
  const start = page * rowsPerPage + 1;
  const end = Math.min((page + 1) * rowsPerPage, count);

  const handleRowsPerPageChange = (value: string) => {
    if (onRowsPerPageChange) {
      const event = {
        target: { value }
      } as React.ChangeEvent<HTMLSelectElement>;
      onRowsPerPageChange(event);
    }
  };

  return (
    <div className="flex flex-col gap-4 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-4 text-sm text-muted-foreground">
        {onRowsPerPageChange && (
          <div className="flex items-center gap-2">
            <span>Rows per page:</span>
            <Select value={String(rowsPerPage)} onValueChange={handleRowsPerPageChange}>
              <SelectTrigger className="h-8 w-[70px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {rowsPerPageOptions.map((option) => (
                  <SelectItem key={option} value={String(option)}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {showDenseToggle && onChangeDense && (
          <label className="flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked={dense}
              onChange={onChangeDense}
              className="h-4 w-4 rounded border-input"
            />
            Dense
          </label>
        )}
      </div>

      <div className="flex items-center justify-between gap-4 sm:justify-end">
        <span className="text-sm text-muted-foreground">
          {count === 0 ? '0 of 0' : `${start}-${end} of ${count}`}
        </span>

        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onPageChange(null, 0)}
            disabled={page === 0}
            title="First page"
          >
            <ChevronsLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onPageChange(null, page - 1)}
            disabled={page === 0}
            title="Previous page"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onPageChange(null, page + 1)}
            disabled={page >= totalPages - 1}
            title="Next page"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onPageChange(null, totalPages - 1)}
            disabled={page >= totalPages - 1}
            title="Last page"
          >
            <ChevronsRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
